import React, { type ElementRef, useEffect, useRef, useState, type FC } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { ExitingPersistence, FadeIn, StaggeredEntrance, durations } from '@atlaskit/motion';
import type Select from '@atlaskit/select';

import {
	SpecificAccessRow,
	type SpecificAccessRowProps,
} from './specificAccessRow/SpecificAccessRow.tsx';
import { SpotlightTarget } from '@atlaskit/onboarding';
import Heading from '@atlaskit/heading';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { expValEquals } from '@confluence/feature-experiments';

export type SpecificAccessDetailsProps = {
	/** Array of principal rows to display. May contain null values when filtering is disabled by the feature gate. */
	principals: (SpecificAccessRowProps | null)[];
	isSpecificAccessDialogOpen: boolean;
};

// The modal body already adds 2px of padding.
// Thus to get to 24px of total distance to the footer
// We need 22px of additional padding. There's no token for that :D
// Hence we do padding `space.250` for 20px and another `space.025` of margin for 2px
const specificAccessContainer = xcss({
	paddingBottom: 'space.250',
	marginBottom: 'space.025',
	paddingLeft: 'space.0',
	// by default the ul element has default browser styling
	// we reset the default margin browser styling here
	marginTop: 'space.0',
});

const spotLightContainer = xcss({
	paddingTop: 'space.075',
	paddingBottom: 'space.075',
	paddingRight: 'space.075',
	paddingLeft: 'space.0',
});

const i18n = defineMessages({
	specificAccess: {
		id: 'share-and-restrict-dialog.specific-access-details.heading',
		defaultMessage: 'Specific access',
		description:
			'Section heading for controls that determine which specific users and groups are able to access this content',
	},
});

export const SpecificAccessDetails: FC<SpecificAccessDetailsProps> = ({
	principals,
	isSpecificAccessDialogOpen,
}) => {
	const [completedFadeIns, setCompletedFadeIns] = useState(new Set<string>());
	const refElement = useRef<ElementRef<typeof Select>>(null);

	useEffect(() => {
		if (refElement.current) {
			refElement.current.focus();
		}
	}, []);

	// Filter out null principals that may be present when the persistence_specific_access_gate feature gate is disabled.
	const nonNullPrincipals = principals.filter((p) => p !== null);

	if (nonNullPrincipals.length === 0) {
		return null;
	}

	return (
		<>
			{!isSpecificAccessDialogOpen &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') && (
					<SpotlightTarget name="specificAccess">
						<Box xcss={spotLightContainer}>
							<Heading size="xxsmall" as="h2">
								<FormattedMessage {...i18n.specificAccess} />
							</Heading>
						</Box>
					</SpotlightTarget>
				)}
			<Stack
				space="space.100"
				alignInline="start"
				grow="fill"
				xcss={specificAccessContainer}
				as="ul"
			>
				<ExitingPersistence appear>
					<StaggeredEntrance delayStep={durations.small}>
						{nonNullPrincipals.map((specificAccessRowProps, idx) => (
							<FadeIn
								key={specificAccessRowProps.key}
								duration="medium"
								entranceDirection="top"
								exitDirection="top"
								onFinish={() => {
									setCompletedFadeIns((prev) => {
										prev.add(specificAccessRowProps.key);
										return prev;
									});
								}}
								// Only show animation for entrance so that rows after removals are not animated
								isPaused={completedFadeIns.has(specificAccessRowProps.key)}
							>
								{(fadeInProps) => (
									// CSS did not work here, so using style
									<Box
										as="li"
										{...fadeInProps}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
										style={{ listStyle: 'none', width: '100%', ...fadeInProps.style }}
									>
										<SpecificAccessRow
											{...specificAccessRowProps}
											principalAccess={{
												...specificAccessRowProps.principalAccess,
												focusRef: idx === 1 ? refElement : null,
											}}
										/>
									</Box>
								)}
							</FadeIn>
						))}
					</StaggeredEntrance>
				</ExitingPersistence>
			</Stack>
		</>
	);
};
